<script setup>
import { storeToRefs } from 'pinia';
import { computed, ref, watch, nextTick, onBeforeUnmount } from 'vue';
import { useForm } from '@inertiajs/vue3';

import { useSupportPlanEventStore } from '@/stores/supportPlanEvent';
import { useSupportPlanStore } from '@/stores/supportPlan';
import { useOptionsStore } from '@/stores/options';
import { useUserStore } from '@/stores/user';
import { isRole } from '@/utils/roleHelper';

import NoteManager from '@/components/Notes/NoteManager.vue';
import DocumentManager from '@/components/Documents/DocumentManager.vue';

const { active: supportPlanEventData } = storeToRefs(useSupportPlanEventStore());
const { supportPlanCriteria: supportPlanCriteriaList } = storeToRefs(useSupportPlanStore());
const { programOptions } = storeToRefs(useOptionsStore());
const { profile } = storeToRefs(useUserStore());
const { updateSupportPlanEvent } = useSupportPlanEventStore();

const roles = computed(() => isRole(profile.value));
const canRemoveEventCriteria = computed(
    () => roles.value.superAdmin() || roles.value.organizationAdmin()
);

const blockOptionsList = ['block 1', 'block 2', 'block 3', 'block 4', 'lunch/break', 'outside hours'];

const currentTab = ref('details_and_criteria');
const criteriaEditorForm = ref([]);
const criteriaModalVisible = ref(false);
const selectedCriteriaId = ref(null);

const supportPlanEventDetails = computed(() => supportPlanEventData.value || {});
const eventForm = useForm({
    date: supportPlanEventDetails.value.date || '',
    program_id: supportPlanEventDetails.value.is_program_other == true ? 'other' : (supportPlanEventDetails.value.program_id ?? ''),
    is_program_other: supportPlanEventDetails.value.is_program_other || false,
    other_program_name: supportPlanEventDetails.value.other_program_name || '',
    was_highlight: supportPlanEventDetails.value.was_highlight || false,
    block: Array.isArray(supportPlanEventDetails.value.block) ? [...supportPlanEventDetails.value.block] : [],
});

const saveEventDetails = () => {
    const updatePayload = {
        hash: supportPlanEventDetails.value.hash,
        ...eventForm,
        program_id: eventForm.program_id == 'other' ? null : eventForm.program_id,
        block: eventForm.block,
        criteria: criteriaEditorForm.value,
    };
    updateSupportPlanEvent(updatePayload).catch(console.error);
};

// autosave when blur is triggered or when the user stops typing for 2 seconds
let otherProgramNameSaveTimer = null;

// autosave when the user stops typing for 2 seconds
const scheduleOtherProgramNameAutosave = () => {
    if (otherProgramNameSaveTimer !== null) {
        clearTimeout(otherProgramNameSaveTimer);
    }
    otherProgramNameSaveTimer = setTimeout(() => {
        otherProgramNameSaveTimer = null;
        saveEventDetails();
    }, 2000);
};
// autosave when the user blurs the input
const flushOtherProgramNameAutosave = () => {
    if (otherProgramNameSaveTimer !== null) {
        clearTimeout(otherProgramNameSaveTimer);
        otherProgramNameSaveTimer = null;
    }
    saveEventDetails();
};

// clear the timer when the component is unmounted
onBeforeUnmount(() => {
    if (otherProgramNameSaveTimer !== null) {
        clearTimeout(otherProgramNameSaveTimer);
    }
});

watch(
    () => [supportPlanEventDetails.value, supportPlanCriteriaList.value],
    ([eventData, criteriaList]) => {
        if (!eventData || !Array.isArray(criteriaList)) {
            return;
        }

        eventForm.date = eventData.date || '';
        eventForm.program_id = eventData.is_program_other ? 'other' : (eventData.program_id || '');
        eventForm.is_program_other = eventData.is_program_other || false;
        eventForm.other_program_name = eventData.other_program_name || '';
        eventForm.was_highlight = eventData.was_highlight || false;
        eventForm.block = Array.isArray(eventData.block) ? [...eventData.block] : [];

        criteriaEditorForm.value = eventData.support_plan_event_criteria?.map(existing => {
            const base = criteriaList.find(c => c.id === existing.support_plan_criteria_id);
            return {
                support_plan_criteria_id: existing.support_plan_criteria_id,
                description: base?.description || '',
                title: base?.title || '',
                type: base?.type?.name || '',
                defined_options: base?.defined_options || [],
                selected_option_id: existing.support_plan_criteria_defined_option_id || null,
                measurable_goal_criteria_value: existing.measurable_goal_criteria_value || '',
            };
        }) || [];
    },
    { immediate: true }
);

const blockCheckboxMap = {};
blockOptionsList.forEach(option => {
    blockCheckboxMap[option] = computed({
        get: () => eventForm.block.includes(option),
        set: (checked) => {
            if (checked && !eventForm.block.includes(option)) {
                eventForm.block.push(option);
            } else {
                eventForm.block = eventForm.block.filter(b => b !== option);
            }
            saveEventDetails();
        }
    });
});

const addCriteria = () => {
    const selected = supportPlanCriteriaList.value.find(c => c.id === selectedCriteriaId.value);
    if (!selected || criteriaEditorForm.value.some(c => c.support_plan_criteria_id === selected.id)) {
        return;
    }

    criteriaEditorForm.value.push({
        support_plan_criteria_id: selected.id,
        title: selected.title,
        description: selected.description,
        type: selected.type.name,
        defined_options: selected.defined_options || [],
        selected_option_id: null,
        measurable_goal_criteria_value: '',
    });

    selectedCriteriaId.value = null;
    criteriaModalVisible.value = false;

    nextTick(() => {
        const element = document.getElementById(`criteria-${selected.id}`);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    });

    saveEventDetails();
};

const removeCriteria = (criteriaId) => {
    if (!canRemoveEventCriteria.value) {
        return;
    }
    criteriaEditorForm.value = criteriaEditorForm.value.filter(c => c.support_plan_criteria_id !== criteriaId);
    saveEventDetails();
};

const pathParts = computed(() => {
    const segments = window.location.pathname.split('/');
    const clientId = segments[2];
    const planId = segments[4];
    const eventId = segments[6];

    return {
        paths: {
            client: `/clients/${clientId}`,
            supportPlan: `/clients/${clientId}/support-plan/${planId}`,
            event: `/clients/${clientId}/support-plan/${planId}/events/${eventId}`,
        }
    };
});
</script>

<template>
<MainContentLayout>
    <template #breadcrumb>
        <v-breadcrumbs class="flex gap-4">
            <v-breadcrumbs-item :href="pathParts.paths.client">
                {{ $t('label.client') }}
            </v-breadcrumbs-item>
            <v-icon>mdi-chevron-right</v-icon>
            <v-breadcrumbs-item :href="pathParts.paths.supportPlan">
                {{ $t('label.support_plan') }}
            </v-breadcrumbs-item>
            <v-icon>mdi-chevron-right</v-icon>
            <v-breadcrumbs-item disabled>
                {{ $t('label.event') }}
            </v-breadcrumbs-item>
        </v-breadcrumbs>
    </template>

    <template #title>{{ $t('title.support_plan_event', { name: `${supportPlanEventData?.support_plan?.client?.first_name} ${supportPlanEventData?.support_plan?.client?.last_name}` ?? ''}) }}</template>
    <div>
        <div class="-mt-4 mb-2">
            <v-btn
                small
                href="/clients"
                variant="tonal"
                block>
                <v-icon>mdi-arrow-left</v-icon>
                {{ $t('label.back_to_clients') }}
            </v-btn>
        </div>
        <div class="mb-4">
            <v-tabs
                v-model="currentTab"
                show-arrows>
                <v-tab value="details_and_criteria">{{ $t('label.details') }}</v-tab>
                <v-tab value="notes">{{ $t('label.notes') }}</v-tab>
                <v-tab value="documents">{{ $t('label.documents') }}</v-tab>
            </v-tabs>
        </div>

        <!-- DETAILS AND CRITERIA TAB -->
        <div v-if="currentTab === 'details_and_criteria'" class="grid grid-cols-1 md:grid-cols-2 gap-4 px-4 pt-4">
            <div class="text-lg font-bold col-span-full">{{ $t('label.details') }}</div>
            <StyledDropdown
                v-model="eventForm.program_id"
                searchable
                :options="[...programOptions, { value: 'other', label: $t('label.other') }]"
                :label="$t('label.program')"
                name="program_id"
                @update:model-value="(val) => { eventForm.is_program_other = val === 'other'; saveEventDetails(); }" />
            <div v-if="eventForm.is_program_other">
                <!-- autosave when blur is triggered or when the user stops typing for 2 seconds -->
                <StyledInput
                    :model-value="eventForm.other_program_name"
                    :label="$t('label.enter_program_name')"
                    name="custom_program_name"
                    @update:model-value="(val) => { eventForm.other_program_name = val; scheduleOtherProgramNameAutosave(); }"
                    @blur="flushOtherProgramNameAutosave" />
            </div>
            <StyledDatepicker
                v-model="eventForm.date"
                :label="$t('label.date')"
                name="date"
                @update:model-value="saveEventDetails" />
            <StyledCheckbox
                v-model="eventForm.was_highlight"
                :label="$t('label.highlight')"
                name="was_highlight"
                @update:model-value="saveEventDetails" />
            <div class="col-span-full">
                <label class="block text-sm font-medium text-gray-700 mb-1">
                    {{ $t('label.blocks') }}
                </label>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <StyledCheckbox
                        v-for="option in blockOptionsList"
                        :key="option"
                        v-model="blockCheckboxMap[option]"
                        :label="option"
                        :name="option" />
                </div>
            </div>

            <div class="text-lg font-bold col-span-full mt-8">{{ $t('label.criteria') }}</div>
            <div class="flex justify-between items-center mb-4 pb-8 col-span-full">
                <v-btn color="primary" class="text-white" @click="criteriaModalVisible = true">
                    {{ $t('label.add_criteria') }}
                </v-btn>
            </div>
            <div
                v-for="(criteria, index) in criteriaEditorForm"
                :id="`criteria-${criteria.support_plan_criteria_id}`"
                :key="criteria.support_plan_criteria_id"
                class="p-4 mb-4 border-t bg-white col-span-full">
                <div class="flex justify-between items-start mb-4">
                    <div>
                        <div class="font-semibold">{{ criteria.title }}</div>
                        <div class="text-gray-500">{{ criteria.description }}</div>
                    </div>
                    <StyledButton
                        v-if="canRemoveEventCriteria"
                        variant="text"
                        color="red"
                        @click="removeCriteria(criteria.support_plan_criteria_id)">
                        {{ $t('label.remove') }}
                    </StyledButton>
                </div>
                <StyledDropdown
                    v-if="criteria.type === 'Defined Criteria'"
                    v-model="criteria.selected_option_id"
                    :options="criteria.defined_options.map(opt => ({ value: opt.id, label: opt.defined_criteria_name }))"
                    :name="`defined_criteria_${index}`"
                    :label="$t('label.select_option')"
                    @input="saveEventDetails" />
                <v-text-field
                    v-else-if="criteria.type === 'Measurable Criteria'"
                    :model-value="criteria.measurable_goal_criteria_value"
                    :name="`measurable_criteria_${index}`"
                    :label="$t('label.enter_value')"
                    :clearable="false"
                    @update:model-value="val => criteria.measurable_goal_criteria_value = val"
                    @blur="saveEventDetails" />
            </div>
        </div>
        <!-- NOTES TAB -->
        <div v-else-if="currentTab === 'notes'" class="mt-4">
            <NoteManager v-if="supportPlanEventData?.hash" type="support-plan-event" :noteable="supportPlanEventData" />
        </div>
        <!-- DOCUMENTS TAB -->
        <div v-else-if="currentTab === 'documents'" class="mt-4">
            <DocumentManager v-if="supportPlanEventData?.hash" type="support-plan-event" :documentable="supportPlanEventData" />
        </div>
    </div>

    <!-- CRITERIA MODAL -->
    <v-dialog v-model="criteriaModalVisible" max-width="400px">
        <v-card>
            <v-card-title class="text-h6">{{ $t('label.add_criteria') }}</v-card-title>
            <v-card-text>
                <v-select
                    v-model="selectedCriteriaId"
                    :items="supportPlanCriteriaList
                        .filter(c => !criteriaEditorForm.some(f => f.support_plan_criteria_id === c.id))
                        .map(c => ({ value: c.id, title: c.title }))"
                    item-title="title"
                    item-value="value"
                    :label="$t('label.select_criteria')" />
            </v-card-text>
            <v-card-actions>
                <v-spacer />
                <v-btn @click="criteriaModalVisible = false">{{ $t('label.cancel') }}</v-btn>
                <v-btn color="primary" @click="addCriteria">{{ $t('label.add') }}</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</MainContentLayout>
</template>
